/**
 * Intelligence Engine - Unit & Math Protocol
 * Centralized logic for unit normalization and price math.
 * Ensures consistency between UI calculations and AI reasoning.
 */

import type { ShelfUnitPrice } from './schemas';

export type UnitSystem = 'imperial' | 'metric';

export const MASS_UNITS_IMPERIAL = ['oz', 'lb'];
export const MASS_UNITS_METRIC = ['g', 'kg'];
export const VOLUME_UNITS_IMPERIAL = ['fl oz', 'pt', 'qt', 'gal'];
export const VOLUME_UNITS_METRIC = ['ml', 'l'];

/**
 * Price per unit — same-unit only, no cross-unit conversion (oz<->lb etc.) yet.
 * A price's own size/unit is enough to compute its own per-unit price regardless
 * of what it's being compared against; callers check unit equality separately
 * before treating two per-unit prices as fairly comparable.
 */
export const computePricePerUnit = (price: number, size: number | string | null | undefined): number | null => {
  const numericSize = typeof size === 'string' ? parseFloat(size) : size;
  if (!numericSize || numericSize <= 0) return null;
  return price / numericSize;
};

// Noise words that should be stripped from slugs to ensure consistency
const STOP_WORDS = [
  'bottle', 'plastic', 'squeeze', 'glass', 'can', 'tin', 'box', 'package', 'pack',
  'bag', 'per', 'unit', 'container', 'tub', 'jar', 'jug',
  'new', 'look', 'original', 'authentic', 'premium', 'best', 'quality',
  'refilled', 'refill', 'value', 'size', 'large', 'small', 'medium',
  'extra', 'super', 'mega'
];

/**
 * Normalizes a string by removing noise and special characters.
 */
export const cleanDescriptor = (text: string | null | undefined): string => {
  if (!text) return '';
  let cleaned = text.toLowerCase().trim();
  
  // Normalization: Unify common units before slugging
  cleaned = cleaned.replace(/\bfl oz\b|\bfloz\b/g, 'floz');
  cleaned = cleaned.replace(/\bea\b|\beach\b/g, 'ct');
  cleaned = cleaned.replace(/\blbs\b/g, 'lb');
  cleaned = cleaned.replace(/\bkilogram\b/g, 'kg');
  cleaned = cleaned.replace(/\bgram\b/g, 'g');

  // PROTECTED: Preserve numeric specs (e.g. 1GB, 500ml) by temporarily hiding them
  const NUMERIC_SPECS = cleaned.match(/\b\d+(\.\d+)?[a-zA-Z]+\b/g) || [];
  NUMERIC_SPECS.forEach((spec, i) => {
    cleaned = cleaned.replace(spec, `__NUMSPEC_${i}__`);
  });

  // PROTECTED PHRASES
  const PROTECTED = [
    { raw: /new york/g, safe: 'newyork_safe' },
    { raw: /steak/g, safe: 'steak_safe' }
  ];

  PROTECTED.forEach(p => {
    cleaned = cleaned.replace(p.raw, p.safe);
  });

  // Remove stop words
  STOP_WORDS.forEach(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'g');
    cleaned = cleaned.replace(regex, '');
  });

  // RESTORE PROTECTED PHRASES
  PROTECTED.forEach(p => {
    cleaned = cleaned.replace(new RegExp(p.safe, 'g'), p.raw.source.replace('/g', ''));
  });

  // RESTORE NUMERIC SPECS
  NUMERIC_SPECS.forEach((spec, i) => {
    cleaned = cleaned.replace(`__NUMSPEC_${i}__`, spec);
  });

  // Preserve alphanumeric characters, hyphens, and DOTS (for decimals)
  return cleaned.replace(/[^a-z0-9.-]+/g, ' ').trim();
};

/**
 * Standardizes a string into a URL-friendly slug.
 */
export const slugify = (text: string): string => {
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '-')           // Replace spaces with -
    .replace(/[^\w.-]+/g, '')       // Remove all non-word chars except dots and hyphens
    .replace(/--+/g, '-')           // Replace multiple - with single -
    .replace(/^-+/, '')             // Trim - from start of text
    .replace(/-+$/, '');            // Trim - from end of text
};

/**
 * Common produce items that should never be treated as brands.
 */
const PRODUCE_NAMES = [
  'banana', 'bananas', 'lemon', 'lemons', 'lime', 'limes', 'apple', 'apples', 
  'orange', 'oranges', 'onion', 'onions', 'potato', 'potatoes', 'tomato', 'tomatoes',
  'avocado', 'avocados', 'cucumber', 'cucumbers', 'pepper', 'peppers', 'carrot', 'carrots',
  'garlic', 'ginger', 'grape', 'grapes', 'berry', 'berries', 'strawberry', 'strawberries',
  'blueberry', 'berries', 'raspberry', 'blackberry', 'melon', 'melons', 'watermelon',
  'peach', 'peaches', 'pear', 'pears', 'plum', 'plums', 'kiwi', 'kiwis', 'mango', 'mangoes'
];

/**
 * Formats a structured item into a human-readable display name.
 * Format: [Brand/Entity] ([Variety/Descriptors], [Size/Quantity/Unit])
 * If brand exists, Entity = Brand, and Product moves into descriptors.
 * If brand is null, Entity = Product.
 */
export const formatDisplayName = (item: {
  brand?: string | null;
  product?: string;
  variety?: string | null;
  size?: number | string | null;
  unit?: string | null;
}): string => {
  const brandValue = (item.brand || '').trim();
  const productValue = (item.product || '').trim();
  const varietyValue = (item.variety || '').trim();
  
  // PRIMARY ENTITY: If brand exists, it is the primary entity. 
  // If no brand exists, the product name is the primary entity.
  const entity = brandValue || productValue || '';
  const isBrandEntity = !!brandValue;
  
  const descriptors: string[] = [];
  
  // Construct the size string for comparison to avoid duplication
  let sizeStr = '';
  if (item.size) {
    if (typeof item.size === 'number') {
      sizeStr = `${item.size}${item.unit || ''}`;
    } else {
      const s = item.size.toString();
      const u = (item.unit || '').toString();
      sizeStr = (u && !s.toLowerCase().includes(u.toLowerCase())) ? `${s}${u}` : s;
    }
  }

  const normalizedSizeStr = sizeStr.toLowerCase().replace(/\s+/g, '');

  // Add Product as descriptor if it's not the entity and not the size
  if (isBrandEntity && productValue && productValue !== entity) {
    const normProduct = productValue.toLowerCase().replace(/\s+/g, '');
    if (normProduct !== normalizedSizeStr) {
      descriptors.push(productValue);
    }
  }
  
  // Add Variety if not the size and not already in descriptors
  if (varietyValue) {
    const normVariety = varietyValue.toLowerCase().replace(/\s+/g, '');
    if (normVariety !== normalizedSizeStr && !descriptors.some(d => d.toLowerCase().replace(/\s+/g, '') === normVariety)) {
      descriptors.push(varietyValue);
    }
  }

  // Add Size last
  if (sizeStr) {
    descriptors.push(sizeStr);
  }
  
  // FINAL DEDUPLICATION: Ensure no duplicate strings in descriptors
  const uniqueDescriptors = Array.from(new Set(descriptors.filter(Boolean)));
  const descriptorString = uniqueDescriptors.join(', ');
  
  if (!descriptorString) return entity.trim();

  // DOUBLE-WRAP PROTECTION: If the entity already contains the descriptor string in brackets,
  // or if the entity itself looks like a fully formatted name, don't wrap it again.
  if (entity.includes('(') && entity.includes(')')) {
    const existingContent = entity.match(/\((.+)\)/)?.[1] || '';
    if (existingContent.toLowerCase().includes(descriptorString.toLowerCase())) {
        return entity.trim();
    }
  }

  return `${entity} (${descriptorString})`.trim();
};

/**
 * Common meat items that should never be treated as brands.
 */
const MEAT_PRODUCT_NAMES = [
  'beef', 'pork', 'chicken', 'turkey', 'lamb', 'steak', 'rib', 'ribs', 'roast', 'ground',
  'breast', 'thigh', 'wing', 'drumstick', 'tenderloin', 'sirloin', 'chuck', 'brisket', 'patty',
  'bacon', 'sausage', 'ham', 'salami', 'pepperoni', 'meatball', 'meatballs'
];

/**
 * Destructures a raw name string into its constituent parts.
 * Follows the Sovereign Protocol for name parsing: Brand (Product, Variety, Size)
 * Used strictly as a fallback for missing database metadata.
 */
export const destructureName = (name: string): {
  brand: string | null;
  product: string;
  variety: string | null;
  size: number | string | null;
  unit: string | null;
} => {
  const cleanedName = name.replace(/\s*Verified\s*$/i, '').trim();
  
  let brand: string | null = null;
  let product: string = cleanedName;
  let variety: string | null = null;
  let size: number | string | null = null;
  let unit: string | null = null;

  if (cleanedName.includes('(')) {
    const match = cleanedName.match(/^([^(]+)\((.+)\)$/);
    if (match) {
      const entity = match[1].trim();
      const detailsStr = match[2].trim();
      const details = detailsStr.split(',').map(s => s.trim());
      
      if (entity) {
        const isMeasurement = (s: string) => /^\d+(\.\d+)?\s*(lb|lbs|oz|ct|g|kg|ml|l|floz|each|ea|count|cnt)$/i.test(s);
        
        const isKnownProduce = PRODUCE_NAMES.includes(entity.toLowerCase()) || 
                              PRODUCE_NAMES.some(p => entity.toLowerCase().includes(p));
        const isKnownMeat = MEAT_PRODUCT_NAMES.includes(entity.toLowerCase()) || 
                           MEAT_PRODUCT_NAMES.some(m => entity.toLowerCase().includes(m));
        
        const firstDetailIsMeasurement = isMeasurement(details[0]);
        
        if (isKnownProduce || isKnownMeat || firstDetailIsMeasurement) {
            brand = null;
            product = entity;
            if (!firstDetailIsMeasurement) {
                variety = details[0];
            }
        } else {
            brand = entity;
            product = details[0] || '';
            if (details.length > 1 && !isMeasurement(details[1])) {
                variety = details[1];
            }
        }
        
        for (let i = details.length - 1; i >= 0; i--) {
          const detail = details[i];
          if (detail && /\d/.test(detail)) {
            const sizeMatch = detail.match(/^([\d.]+)\s*([a-zA-Z\s]+)$/);
            if (sizeMatch) {
              size = parseFloat(sizeMatch[1]);
              const rawUnit = sizeMatch[2].trim().toLowerCase();
              unit = (rawUnit === 'ea' || rawUnit === 'each' || rawUnit === 'count') ? 'ct' : rawUnit;
              break;
            } else if (isMeasurement(detail)) {
              size = detail;
              break;
            }
          }
        }
      }
    }
  }

  return { brand, product, variety, size, unit };
};

/**
 * Constructs a deterministic canonicalId (slug) for an item.
 * Format: [brand]-[product]-[variety]-[size][unit]
 */
export const constructProtocolSlug = (item: {
  brand?: string | null;
  product?: string;
  variety?: string | null;
  size?: number | string | null;
  unit?: string | null;
}): string => {
  let rawBrand = item.brand || 'generic';
  let rawProduct = item.product || '';
  let rawVariety = item.variety || '';
  let rawSize = item.size;
  let rawUnit = item.unit;

  // SOVEREIGN HEURISTIC: If brand is generic but product contains a parenthetical, 
  // it might be the "Brand (Product, Variety, Size)" pattern from a manual entry.
  if ((!item.brand || item.brand.toLowerCase() === 'generic') && rawProduct.includes('(')) {
    const match = rawProduct.match(/^([^(]+)\(([^)]+)\)$/);
    if (match) {
      const entity = match[1].trim();
      const details = match[2].split(',').map(s => s.trim());
      
      // If we found a brand-like entity before the bracket
      if (entity) {
        rawBrand = entity;
        rawProduct = details[0] || '';
        if (details.length > 1) {
          rawVariety = details[1];
        }
        // If details contains something that looks like size/unit (e.g. "12oz")
        const lastDetail = details[details.length - 1];
        if (lastDetail && /\d/.test(lastDetail)) {
          // Only override size if the manual size field was empty
          if (!rawSize) {
            const sizeMatch = lastDetail.match(/^([\d.]+)([a-zA-Z\s]+)$/);
            if (sizeMatch) {
              rawSize = sizeMatch[1];
              rawUnit = sizeMatch[2];
            } else {
              rawSize = lastDetail;
            }
          }
        }
      }
    }
  }

  const brand = slugify(rawBrand);
  const product = slugify(cleanDescriptor(rawProduct));
  const variety = slugify(cleanDescriptor(rawVariety));
  
  let sizePart = '';
  if (rawSize) {
    if (typeof rawSize === 'number') {
      sizePart = `${rawSize}${slugify(rawUnit || '')}`;
    } else {
      // If it's already a string (e.g. "16oz"), we still append the unit if it's missing from the string
      const sizeSlug = slugify(rawSize);
      const unitSlug = slugify(rawUnit || '');
      sizePart = (unitSlug && !sizeSlug.endsWith(unitSlug)) ? `${sizeSlug}${unitSlug}` : sizeSlug;
    }
  } else if (rawUnit) {
    // Case where there is only a unit (e.g. "per lb")
    sizePart = slugify(rawUnit);
  }

  return [brand, product, variety, sizePart].filter(Boolean).join('-');
};

/**
 * SOVEREIGN IDENTITY GATEWAY
 * The single source of truth for generating item identity.
 */
export const generateItemIdentity = (item: {
  brand?: string | null;
  product?: string;
  variety?: string | null;
  size?: number | string | null;
  unit?: string | null;
}) => {
  return {
    name: formatDisplayName(item),
    canonicalId: constructProtocolSlug(item)
  };
};

/**
 * Normalizes a unit value to a base unit for comparison.
 * Base Units: grams (g) for mass, milliliters (ml) for volume, count (ct) for discrete.
 */
export const normalizeToSubUnit = (value: number, unit: string): { normalizedValue: number, baseUnit: string } => {
  const u = unit.toLowerCase().trim();
  
  // Mass
  if (u === 'lb' || u === 'lbs' || u === 'pound' || u === 'pounds') return { normalizedValue: value * 453.592, baseUnit: 'g' };
  if (u === 'oz' || u === 'ounce' || u === 'ounces') return { normalizedValue: value * 28.3495, baseUnit: 'g' };
  if (u === 'kg' || u === 'kilogram') return { normalizedValue: value * 1000, baseUnit: 'g' };
  if (u === 'g' || u === 'gram') return { normalizedValue: value, baseUnit: 'g' };
  if (u === 'mg') return { normalizedValue: value / 1000, baseUnit: 'g' };

  // Volume
  if (u === 'gal' || u === 'gallon') return { normalizedValue: value * 3785.41, baseUnit: 'ml' };
  if (u === 'qt' || u === 'quart') return { normalizedValue: value * 946.353, baseUnit: 'ml' };
  if (u === 'pt' || u === 'pint') return { normalizedValue: value * 473.176, baseUnit: 'ml' };
  if (u === 'fl oz' || u === 'floz') return { normalizedValue: value * 29.5735, baseUnit: 'ml' };
  if (u === 'l' || u === 'liter' || u === 'litre') return { normalizedValue: value * 1000, baseUnit: 'ml' };
  if (u === 'ml' || u === 'milliliter') return { normalizedValue: value, baseUnit: 'ml' };

  // Discrete
  return { normalizedValue: value, baseUnit: 'ct' };
};

/**
 * Calculates the size ratio between two items.
 * Ratio = Size A / Size B (Normalized to same base unit)
 */
export const calculateNormalizedRatio = (
  sizeA: number, unitA: string,
  sizeB: number, unitB: string
): number => {
  const normA = normalizeToSubUnit(sizeA, unitA);
  const normB = normalizeToSubUnit(sizeB, unitB);

  // If base units don't match (e.g. mass vs volume), we can't accurately calculate ratio
  // unless we assume density (usually 1:1 for water-like liquids).
  if (normA.baseUnit !== normB.baseUnit) return 1.0;

  return normA.normalizedValue / normB.normalizedValue;
};

/**
 * Precision Math: Formats a currency value
 */
export const formatCurrency = (value: number, symbol: string = '$'): string => {
  return `${symbol}${value.toFixed(2)}`;
};

/**
 * Comparison Logic: Determines the better value
 */
export const getBetterValueIndex = (
  priceA: number, sizeA: number, unitA: string,
  priceB: number, sizeB: number, unitB: string
): 'A' | 'B' | 'TIE' => {
  const ratio = calculateNormalizedRatio(sizeA, unitA, sizeB, unitB);
  const normalizedPriceA = priceA / ratio;
  const normalizedPriceB = priceB;

  if (Math.abs(normalizedPriceA - normalizedPriceB) < 0.01) return 'TIE';
  return normalizedPriceA < normalizedPriceB ? 'A' : 'B';
};

// ── Shelf unit-price check ────────────────────────────────────────────────
// A shelf tag prints the price and a unit price ("$4.99 · $0.28/oz"); the
// check redoes that math from the price and the size and flags the item when
// the printed unit price doesn't add up. Weight (oz) and liquid volume (fl oz)
// never convert into each other; a unit missing from this table is never
// compared (so "per sheet" is never taken for "per ct").

const UNIT_TABLE: Record<string, { dim: string; factor: number }> = {
  // mass, in grams
  mg: { dim: 'mass', factor: 0.001 }, g: { dim: 'mass', factor: 1 }, gram: { dim: 'mass', factor: 1 }, grams: { dim: 'mass', factor: 1 },
  kg: { dim: 'mass', factor: 1000 }, kilogram: { dim: 'mass', factor: 1000 }, kilograms: { dim: 'mass', factor: 1000 },
  oz: { dim: 'mass', factor: 28.3495 }, ounce: { dim: 'mass', factor: 28.3495 }, ounces: { dim: 'mass', factor: 28.3495 },
  lb: { dim: 'mass', factor: 453.592 }, lbs: { dim: 'mass', factor: 453.592 }, pound: { dim: 'mass', factor: 453.592 }, pounds: { dim: 'mass', factor: 453.592 },
  // volume, in millilitres
  ml: { dim: 'volume', factor: 1 }, milliliter: { dim: 'volume', factor: 1 }, millilitre: { dim: 'volume', factor: 1 },
  cl: { dim: 'volume', factor: 10 }, dl: { dim: 'volume', factor: 100 },
  l: { dim: 'volume', factor: 1000 }, liter: { dim: 'volume', factor: 1000 }, litre: { dim: 'volume', factor: 1000 }, liters: { dim: 'volume', factor: 1000 }, litres: { dim: 'volume', factor: 1000 },
  floz: { dim: 'volume', factor: 29.5735 }, 'fl oz': { dim: 'volume', factor: 29.5735 }, 'fluid ounce': { dim: 'volume', factor: 29.5735 }, 'fluid ounces': { dim: 'volume', factor: 29.5735 },
  pt: { dim: 'volume', factor: 473.176 }, pint: { dim: 'volume', factor: 473.176 },
  qt: { dim: 'volume', factor: 946.353 }, quart: { dim: 'volume', factor: 946.353 },
  gal: { dim: 'volume', factor: 3785.41 }, gallon: { dim: 'volume', factor: 3785.41 },
  // count
  ct: { dim: 'count', factor: 1 }, count: { dim: 'count', factor: 1 }, ea: { dim: 'count', factor: 1 }, each: { dim: 'count', factor: 1 },
  pc: { dim: 'count', factor: 1 }, pcs: { dim: 'count', factor: 1 }, piece: { dim: 'count', factor: 1 }, pieces: { dim: 'count', factor: 1 }, unit: { dim: 'count', factor: 1 },
  // length, in metres; area, in square metres
  in: { dim: 'length', factor: 0.0254 }, ft: { dim: 'length', factor: 0.3048 }, yd: { dim: 'length', factor: 0.9144 },
  cm: { dim: 'length', factor: 0.01 }, m: { dim: 'length', factor: 1 },
  'sq ft': { dim: 'area', factor: 0.092903 }, sqft: { dim: 'area', factor: 0.092903 }, 'sq m': { dim: 'area', factor: 1 }, sqm: { dim: 'area', factor: 1 }, m2: { dim: 'area', factor: 1 },
};

/** "fl. oz." / "Lbs" / "100 G" → a UNIT_TABLE key, or null. */
const normalizeUnitKey = (unit: string): string | null => {
  const u = unit.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim()
    .replace(/^per /, '').replace(/^fl ?oz$/, 'fl oz').replace(/^sq ?ft$/, 'sq ft').replace(/^sq ?m$/, 'sq m');
  return UNIT_TABLE[u] ? u : null;
};

/**
 * Splits a printed unit-price unit into how much it's per and the unit:
 * "oz" → 1 oz, "100 g" → 100 g, "per 100 ct" → 100 ct, "1kg" → 1 kg.
 */
export const parseUnitPriceUnit = (raw: string | null | undefined): { per: number; unit: string } | null => {
  if (!raw) return null;
  const m = String(raw).toLowerCase().replace(/^\s*(per|\/)\s*/, '').trim().match(/^([\d.,]+)?\s*(.+)$/);
  if (!m) return null;
  const per = m[1] ? parseFloat(m[1].replace(',', '.')) : 1;
  const key = normalizeUnitKey(m[2]);
  if (!key || !(per > 0)) return null;
  return { per, unit: key };
};

/** How many `to` units there are in `value` `from` units, or null when they don't convert. */
export const convertUnitQuantity = (value: number, from: string, to: string): number | null => {
  const a = normalizeUnitKey(from), b = normalizeUnitKey(to);
  if (!a || !b) return null;
  const ua = UNIT_TABLE[a], ub = UNIT_TABLE[b];
  if (ua.dim !== ub.dim) return null;
  return (value * ua.factor) / ub.factor;
};

export type UnitPriceBasis = 'sale' | 'regular' | 'member';
export type ReadConfidence = 'high' | 'medium' | 'low';

/** What the reader returns for one line (all optional: most images print no unit price). */
export interface UnitPriceReading {
  price?: number | null;
  size?: number | string | null;
  unit?: string | null;
  quantity?: number | null;
  shelfUnitPrice?: number | null;
  shelfUnitPriceUnit?: string | null;
  shelfUnitPriceBasis?: UnitPriceBasis | null;
  priceBasis?: UnitPriceBasis | null;
  regularPrice?: number | null;
  memberPrice?: number | null;
  isMultiBuy?: boolean | null;
  sizeConfidence?: ReadConfidence | null;
  unitPriceConfidence?: ReadConfidence | null;
}

/**
 * The check as shown: what the tag printed against CartLens's own price ÷ size,
 * worked out from the item's current fields (compareUnitPrice), never stored.
 */
export interface UnitPriceCheck {
  status: 'match' | 'mismatch';
  source: 'tag' | 'receipt';
  /** As printed: shelfUnitPrice per `shelfPer` `shelfUnit` (e.g. 0.31 per 1 oz). */
  shelfUnitPrice: number;
  shelfPer: number;
  shelfUnit: string;
  basis: UnitPriceBasis | null;
  /** False when the unit price belongs to another price on the tag (e.g. the regular price beside a sale price). */
  usesItemPrice: boolean;
  /** CartLens's own unit price, in the printed unit. */
  calculatedUnitPrice: number;
  /** The formula inputs: price ÷ size (converted into shelfUnit). */
  price: number;
  size: number;
  sizeUnit: string;
  /** (calculated - printed) / printed, as a fraction. */
  difference: number;
}

// Flag only past this relative gap…
const UNIT_PRICE_TOLERANCE = 0.02;
// …and past one step of the printed unit price's own rounding ($0.31 → $0.01).
const displayStep = (value: number): number => {
  const decimals = Math.min(4, (String(value).split('.')[1] || '').length);
  return Math.max(Math.pow(10, -decimals), 0.001);
};
// A gap of exactly one conversion factor means the unit was misread, not mispriced.
const UNIT_SLIP_FACTORS = [10, 16, 28.3495, 100, 453.592, 1000, 29.5735, 33.814, 2.20462, 35.274];
const looksLikeUnitSlip = (ratio: number): boolean =>
  UNIT_SLIP_FACTORS.some(f => Math.abs(ratio / f - 1) < 0.03 || Math.abs(ratio * f - 1) < 0.03);

const toNumber = (v: number | string | null | undefined): number =>
  typeof v === 'string' ? parseFloat(v) : Number(v);

/**
 * What a tag (or a weighed receipt line) printed, as saved on the item. Null
 * when there's nothing reliable to keep: no unit price, a unit-price read below
 * high confidence, a unit that isn't in the table, or a multi-buy or member
 * price that can't be matched to its own unit price.
 *
 * `source: 'receipt'` is a weighed receipt line ("2.44 lb @ $0.99/lb … $2.42"):
 * the amount charged is the weight times the rate, rounded to the cent.
 */
export const readShelfUnitPrice = (r: UnitPriceReading, source: 'tag' | 'receipt'): ShelfUnitPrice | null => {
  const shelf = Number(r.shelfUnitPrice);
  const parsedUnit = parseUnitPriceUnit(r.shelfUnitPriceUnit);
  if (!(shelf > 0) || !parsedUnit) return null;
  // A receipt prints the weight and rate on the line (and its totals are
  // reconciled), so an unset confidence there is fine; anything below high isn't.
  const sure = (c: ReadConfidence | null | undefined) => c === 'high' || (source === 'receipt' && c == null);
  if (!sure(r.unitPriceConfidence)) return null;

  // Which shelf price the unit price belongs to.
  const unitBasis = r.shelfUnitPriceBasis || null;
  const priceBasis = r.priceBasis || null;
  const basis: UnitPriceBasis | null = unitBasis || priceBasis;
  let otherPrice: number | undefined;
  if (source === 'receipt') {
    if ((Number(r.quantity) || 1) !== 1) return null; // a weighed line is one line total
  } else if (!unitBasis || !priceBasis || unitBasis === priceBasis) {
    // Same price (or a plain tag with one price): the item's own price.
    if (r.isMultiBuy) return null; // "2 for $5": the per-item price is derived, not printed
    if (basis === 'member' && unitBasis !== priceBasis) return null;
  } else if (unitBasis === 'regular' || unitBasis === 'member') {
    otherPrice = Number(unitBasis === 'regular' ? r.regularPrice : r.memberPrice) || undefined;
    if (!otherPrice) return null;
  } else {
    return null; // a sale unit price but the main price isn't the sale price
  }

  const readSize = toNumber(r.size);
  return {
    source,
    shelfUnitPrice: shelf,
    shelfPer: parsedUnit.per,
    shelfUnit: parsedUnit.unit,
    basis,
    usesItemPrice: otherPrice === undefined,
    ...(otherPrice !== undefined ? { otherPrice } : {}),
    ...(readSize > 0 ? { readSize } : {}),
    ...(r.unit ? { readSizeUnit: r.unit } : {}),
    sizeTrusted: sure(r.sizeConfidence),
  };
};

/**
 * The check for an item as it is now: the tag's printed unit price against
 * price ÷ size from the item's current fields. Null when it can't be judged:
 * no size, a unit that doesn't convert (oz vs fl oz), the size the reader wasn't
 * sure of and the shopper hasn't changed, or a gap that is exactly a unit slip.
 */
export const compareUnitPrice = (
  tag: ShelfUnitPrice,
  item: { price?: number | null; size?: number | string | null; unit?: string | null; quantity?: number | null },
): UnitPriceCheck | null => {
  const size = toNumber(item.size);
  const unit = item.unit || '';
  if (!(size > 0) || !unit) return null;
  // Saved by earlier builds: a snapshot of the whole check, with the compared
  // price and size in it.
  const usesItemPrice = tag.usesItemPrice !== false;
  const otherPrice = tag.otherPrice ?? (usesItemPrice ? undefined : tag.price);
  const readSize = tag.readSize ?? tag.size;
  const readSizeUnit = tag.readSizeUnit ?? tag.sizeUnit;
  const sizeAsRead = readSize === size && readSizeUnit === unit;
  if (tag.sizeTrusted === false && sizeAsRead) return null;
  if (tag.dismissed && sizeAsRead && tag.price === item.price) return null;
  if (tag.source === 'receipt' && (Number(item.quantity) || 1) !== 1) return null;

  const price = usesItemPrice ? Number(item.price) : Number(otherPrice);
  if (!(price > 0)) return null;
  const shelf = tag.shelfUnitPrice;
  const sizeInShelfUnits = convertUnitQuantity(size, unit, tag.shelfUnit);
  if (!sizeInShelfUnits || !(sizeInShelfUnits > 0)) return null;
  const calculated = (price / sizeInShelfUnits) * tag.shelfPer;
  const ratio = calculated / shelf;
  const difference = ratio - 1;

  let mismatch: boolean;
  if (tag.source === 'receipt') {
    // Weighed line: compare what was charged with weight × rate, allowing a
    // cent of rounding on the total and half a step on the printed weight.
    const expected = (sizeInShelfUnits / tag.shelfPer) * shelf;
    const gap = Math.abs(price - expected);
    const weightStep = displayStep(size) / 2;
    const allowed = 0.01 + (convertUnitQuantity(weightStep, unit, tag.shelfUnit) || 0) / tag.shelfPer * shelf;
    mismatch = gap > allowed && gap / expected > UNIT_PRICE_TOLERANCE;
  } else {
    mismatch = Math.abs(calculated - shelf) > displayStep(shelf) && Math.abs(difference) > UNIT_PRICE_TOLERANCE;
  }
  if (mismatch && looksLikeUnitSlip(ratio)) return null;

  return {
    status: mismatch ? 'mismatch' : 'match',
    source: tag.source,
    shelfUnitPrice: shelf,
    shelfPer: tag.shelfPer,
    shelfUnit: tag.shelfUnit,
    basis: tag.basis ?? null,
    usesItemPrice,
    calculatedUnitPrice: calculated,
    price,
    size,
    sizeUnit: unit,
    difference,
  };
};

/** Reads a line's printed unit price and checks it against the line as read. */
export const checkUnitPrice = (r: UnitPriceReading, source: 'tag' | 'receipt'): UnitPriceCheck | null => {
  const tag = readShelfUnitPrice(r, source);
  return tag ? compareUnitPrice(tag, r) : null;
};

/**
 * The size that makes the tag's unit price add up ("Looks right" on a
 * mismatch: the tag is right, so the size was misread), in the item's own unit:
 * $5.99 ÷ $0.25/oz = 23.96 → 24 oz. Rounded to the coarsest step (whole units,
 * then halves, tenths, hundredths) that still matches the tag. Null when the
 * price or unit can't give one.
 */
export const sizeFromShelfUnitPrice = (
  tag: ShelfUnitPrice,
  item: { price?: number | null; size?: number | string | null; unit?: string | null; quantity?: number | null },
): number | null => {
  const usesItemPrice = tag.usesItemPrice !== false;
  const price = usesItemPrice ? Number(item.price) : Number(tag.otherPrice ?? tag.price);
  if (!(price > 0)) return null;
  const inShelfUnits = (price / tag.shelfUnitPrice) * tag.shelfPer;
  const unit = item.unit || tag.shelfUnit;
  const exact = convertUnitQuantity(inShelfUnits, tag.shelfUnit, unit);
  if (!exact || !(exact > 0)) return null;
  // A shopper-chosen size counts as trusted when the check is re-run on it.
  const trusted = { ...tag, sizeTrusted: true, dismissed: false };
  for (const step of [1, 0.5, 0.25, 0.1, 0.05, 0.01]) {
    const size = Math.round(exact / step) * step;
    if (!(size > 0)) continue;
    const rounded = Math.round(size * 100) / 100;
    if (compareUnitPrice(trusted, { ...item, size: rounded, unit })?.status === 'match') return rounded;
  }
  return Math.round(exact * 100) / 100;
};

/**
 * Reads the weight and rate of a weighed receipt line as printed, e.g.
 * "2.44 lb @ 0.99 / lb", "1.24 lb @ 1 lb /0.78", "0.512 kg @ $2.99/kg".
 * Null when the line isn't a weighed line.
 */
export const parseWeighedLine = (text: string | null | undefined): { weight: number; unit: string; rate: number; rateUnit: string } | null => {
  if (!text) return null;
  const u = '(lbs?|kg|oz|g)';
  const m = String(text).toLowerCase().match(new RegExp(
    `(\\d+(?:\\.\\d+)?)\\s*${u}\\s*@\\s*(?:(?:1\\s*)?${u}\\s*\\/\\s*\\$?(\\d+(?:\\.\\d+)?)|\\$?(\\d+(?:\\.\\d+)?)\\s*\\/\\s*${u})`));
  if (!m) return null;
  const weight = parseFloat(m[1]);
  const unit = m[2].replace(/^lbs$/, 'lb');
  const rate = parseFloat(m[4] ?? m[5]);
  const rateUnit = (m[3] ?? m[6]).replace(/^lbs$/, 'lb');
  if (!(weight > 0) || !(rate > 0)) return null;
  return { weight, unit, rate, rateUnit };
};
